import { useEffect, useState, type FormEvent } from "react";
import { Api, ApiError } from "../sync/api";
import { Field, Form } from "./ui";
import { LanguageSwitch } from "./LanguageSwitch";
import { t } from "../i18n";
import { logo, siteName, useBranding } from "../brand";
import { cancelled, credentialJSON, passkeysHere, requestOptions } from "../webauthn";

const api = new Api("/api/v1", null);

const ssoErrors = (): Record<string, string> => ({
  not_provisioned: t("There is no account for you on {name} yet. Ask an administrator.", { name: siteName() }),
  account_inactive: t("Your account is suspended."),
  expired: t("Sign-in took too long. Try again."),
});

// Login signs in; handle, when set, is who just set their password.
export function Login({ onSignedIn, ssoError, handle: given = "" }: { onSignedIn: (token: string) => void; ssoError?: string; handle?: string }) {
  const brand = useBranding();
  const [handle, setHandle] = useState(given);
  const [password, setPassword] = useState("");
  const [error, setError] = useState(ssoError ? (ssoErrors()[ssoError] ?? t("Single sign-on failed.")) : "");
  const [busy, setBusy] = useState(false);
  const [sso, setSSO] = useState<{ name: string; start: string } | null>(null);
  // A sign-in whose password was right, waiting for its second factor.
  const [pending, setPending] = useState("");
  const [methods, setMethods] = useState<string[]>([]);
  const [code, setCode] = useState("");
  // Whether this server takes passkeys, and this browser can use them.
  const [passkeys, setPasskeys] = useState(false);
  // Asking for a link to set a new password: "ask", then "asked"; email is
  // whether the server emails it.
  const [forgot, setForgot] = useState<"" | "ask" | "asked">("");
  const [emailsLinks, setEmailsLinks] = useState(false);

  useEffect(() => {
    api
      .request<{ oidc?: { name: string; start: string }; saml?: { name: string; start: string }; passkeys?: { origin: string }; password_reset?: { email: boolean } }>(
        "GET",
        "/auth/methods",
      )
      .then(
        (m) => {
          setSSO(m.oidc ?? m.saml ?? null);
          setPasskeys(passkeysHere(m));
          setEmailsLinks(!!m.password_reset?.email);
        },
        () => {},
      );
  }, []);

  // withPasskey signs in with a passkey: alone, or as the second step of the
  // pending sign-in.
  async function withPasskey() {
    setBusy(true);
    setError("");
    try {
      const begun = pending ? await api.beginPasskeySecondStep(pending) : await api.beginPasskeySignIn();
      const got = (await navigator.credentials.get({ publicKey: requestOptions(begun.options) })) as PublicKeyCredential | null;
      if (!got) return;
      const answer = credentialJSON(got);
      const r = pending ? await api.finishPasskeySecondStep(pending, begun.pending, answer) : await api.finishPasskeySignIn(begun.pending, answer);
      if (r.token) onSignedIn(r.token);
    } catch (err) {
      if (cancelled(err)) return;
      if (err instanceof ApiError && err.code === "sign_in_expired") {
        setPending("");
        setError(t("Signing in took too long, or had too many wrong codes. Sign in again."));
      } else setError(err instanceof ApiError && err.code !== "passkey_failed" ? err.message : t("Your passkey could not be used here."));
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.login(handle.trim(), password);
      if (r.second_factor) {
        setPending(r.second_factor.pending);
        setMethods(r.second_factor.methods);
        setPassword("");
      } else if (r.token) onSignedIn(r.token);
    } catch (err) {
      setError(err instanceof ApiError && err.code === "too_many_attempts" ? t("Too many attempts. Try again later.") : t("Handle or password is wrong."));
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.secondFactor(pending, code);
      if (r.token) onSignedIn(r.token);
    } catch (err) {
      setCode("");
      if (err instanceof ApiError && err.code === "sign_in_expired") {
        setPending("");
        setError(t("Signing in took too long, or had too many wrong codes. Sign in again."));
      } else if (err instanceof ApiError && err.code === "too_many_attempts") {
        setPending("");
        setError(t("Too many attempts. Try again later."));
      } else setError(err instanceof ApiError ? (err.code === "invalid_code" ? t("That code is wrong or was used already.") : err.message) : t("Could not reach the server."));
    } finally {
      setBusy(false);
    }
  }

  async function ask(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.forgotPassword(handle.trim());
      setForgot("asked");
    } catch (err) {
      setError(err instanceof ApiError && err.code === "too_many_attempts" ? t("Too many attempts. Try again later.") : err instanceof ApiError ? err.message : t("Could not reach the server."));
    } finally {
      setBusy(false);
    }
  }

  if (forgot) {
    const back = () => {
      setForgot("");
      setError("");
    };
    return (
      <main className="page login">
        <div className="card login-card">
          <img className="login-logo" src={logo(192, brand)} alt="" width={44} height={44} />
          <h1>{t("Forgot your password?")}</h1>
          {forgot === "asked" ? (
            <>
              <p className="login-lede" role="status">
                {emailsLinks
                  ? t("If {handle} has an account here with an email address, a link to set a new password is on its way there; it works for an hour. Otherwise an administrator has been asked to make you one.", { handle: handle.trim() })
                  : t("If {handle} has an account here, an administrator has been asked to make you a link to set a new password. They will hand it to you.", { handle: handle.trim() })}
              </p>
              <button type="button" onClick={back}>
                {t("Back to sign in")}
              </button>
            </>
          ) : (
            <>
              <p className="login-lede">
                {emailsLinks ? t("Enter your handle, and we will email you a link to set a new password.") : t("Enter your handle, and we will ask an administrator to make you a link to set a new password.")}
              </p>
              <Form onSubmit={ask}>
                <Field label={t("Handle")}>
                  <input value={handle} onChange={(e) => setHandle(e.target.value)} autoComplete="username" autoCapitalize="none" autoCorrect="off" autoFocus required />
                </Field>
                {error && (
                  <p className="error" role="alert">
                    {error}
                  </p>
                )}
                <button type="submit" disabled={busy}>
                  {busy ? t("Asking…") : t("Ask for a link")}
                </button>
                <button type="button" className="ghost" onClick={back}>
                  {t("Back")}
                </button>
              </Form>
            </>
          )}
        </div>
        <LanguageSwitch />
      </main>
    );
  }

  if (pending) {
    return (
      <main className="page login">
        <div className="card login-card">
          <img className="login-logo" src={logo(192, brand)} alt="" width={44} height={44} />
          <h1>{t("Two-step verification")}</h1>
          <p className="login-lede">{t("Enter the code your authenticator app shows for {name}.", { name: siteName(brand) })}</p>
          <Form onSubmit={verify}>
            <Field label={t("Code")} hint={t("Lost your phone? Enter one of your recovery codes instead.")}>
              <input value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" autoCapitalize="none" spellCheck={false} autoFocus required />
            </Field>
            {error && (
              <p className="error" role="alert">
                {error}
              </p>
            )}
            <button type="submit" disabled={busy}>
              {busy ? t("Checking…") : t("Verify")}
            </button>
            {passkeys && methods.includes("passkey") && (
              <button type="button" onClick={() => void withPasskey()} disabled={busy}>
                {t("Use a passkey instead")}
              </button>
            )}
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setPending("");
                setCode("");
                setError("");
              }}
            >
              {t("Back")}
            </button>
          </Form>
        </div>
        <LanguageSwitch />
      </main>
    );
  }

  return (
    <main className="page login">
      <div className="card login-card">
        <img className="login-logo" src={logo(192, brand)} alt="" width={44} height={44} />
        <h1>{t("Sign in to {name}", { name: siteName(brand) })}</h1>
        <p className="login-lede">
          {given ? t("Your password is set. Sign in with it.") : brand.sign_in_message || t("Your team's messages, tasks and agents, on your own servers.")}
        </p>
        {sso && (
          <>
            <a className="btn primary sso" href={`${sso.start}?return=${encodeURIComponent(location.pathname)}`}>
              {t("Sign in with {name}", { name: sso.name })}
            </a>
            <p className="or">
              <span>{t("or with a local account")}</span>
            </p>
          </>
        )}
        {passkeys && (
          <>
            <button type="button" className="passkey-sign-in" onClick={() => void withPasskey()} disabled={busy}>
              {t("Use a passkey")}
            </button>
            <p className="or">
              <span>{t("or with your password")}</span>
            </p>
          </>
        )}
        <Form onSubmit={submit}>
          <Field label={t("Handle")}>
            <input value={handle} onChange={(e) => setHandle(e.target.value)} autoComplete="username" autoCapitalize="none" autoCorrect="off" autoFocus={!given} required />
          </Field>
          <Field label={t("Password")}>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoFocus={!!given} required />
          </Field>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <button type="submit" disabled={busy}>
            {busy ? t("Signing in…") : t("Sign in")}
          </button>
          <button
            type="button"
            className="ghost"
            onClick={() => {
              setForgot("ask");
              setError("");
            }}
          >
            {t("Forgot password?")}
          </button>
        </Form>
      </div>
      <LanguageSwitch />
    </main>
  );
}
